// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import NavigationModeMenu from "@vanmeter/Overrides/Components/NavigationModeMenu";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@vanmeter/XDL/constants";
import IconEdit from "@vanmeter/XDL/Icons/IconPencil";
import { CustomPageLinkModel } from "@vanmeter/XDL/types";
import { color, ellipsis, filterLinks, hideEmpty, mergeWithCss } from "@vanmeter/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface MiniSignInProps {
    accountManagementLinks?: (CustomPageLinkModel | undefined)[];
    orderManagementLinks?: (CustomPageLinkModel | undefined)[];
    resourcesLinks?: (CustomPageLinkModel | undefined)[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    onCloseMenu?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    const currentLocation = getLocation(state);

    const returnUrl = encodeURIComponent(`${currentLocation.pathname}${currentLocation.search}`);
    const changeCustomerPageUrl =
        (getPageLinkByPageType(state, "ChangeCustomerPage")?.url || "") + `?returnUrl=${returnUrl}`;

    return {
        firstName: session.firstName,
        lastName: session.lastName,
        customerNumber: getBillToState(state, session.billToId).value?.customerNumber,
        companyName: getBillToState(state, session.billToId).value?.companyName,
        isPunchOutSession: getIsPunchOutSession(state),
        changeCustomerPageUrl,
        canViewClosedOrders:
            (session.userRoles || "").includes("Administrator") || (session.userRoles || "").includes("Order History"),
        canViewAccountSummaryAndMakePayemnts:
            (session.userRoles || "").includes("Administrator") || (session.userRoles || "").includes("Finance"),
        isChangeCustomerPage: getCurrentPage(state).type === "ChangeCustomerPage",
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    customerInfoSection?: InjectableCss;
    customerInfoWrapper?: InjectableCss;
    customerNum?: TypographyPresentationProps;
    companyName?: TypographyPresentationProps;
    linksWrapper?: InjectableCss;
    linksContainer?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    linksSection?: InjectableCss;
    link?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
    changeShiptoLink?: LinkPresentationProps;
    signOutLink?: LinkPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    customerInfoWrapper: {
        css: css`
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 12px;
            display: flex;
            flex-direction: column;
            padding: 0 12px 12px;
        `,
    },
    customerInfoSection: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    customerNum: {
        ...fontConfig.xSmallSingleLine,
    },
    companyName: {
        ...fontConfig.xSmallSingleLine,
        css: css`
            max-width: 197px;
            ${ellipsis(2)}
        `,
    },
    linksWrapper: {
        css: css`
            padding: 0 12px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    linksContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 24px;
            ${hideEmpty}
        `,
    },
    sectionTitle: {
        variant: "h5",
    },
    linksSection: {
        css: css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px 24px;
            justify-items: start;
        `,
    },
    link: {
        typographyProps: {
            ...fontConfig.xSmallSingleLine,
        },
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 12px 12px 0;
        `,
    },
    changeShiptoLink: {
        icon: { iconProps: { src: IconEdit, width: 12, height: 13 } as any },
        typographyProps: { ...fontConfig.xSmallSingleLine },
    },
    signOutLink: {
        typographyProps: { ...fontConfig.xSmallSingleLine },
        icon: {
            iconProps: { src: "LogOut", width: 12, height: 13 } as any,
        },
    },
};

const MiniMyAccount: FC<Props> = ({
    accountManagementLinks,
    orderManagementLinks,
    resourcesLinks,
    displayChangeCustomerLink,
    onSignOutHandler,
    onCloseMenu,
    extendedStyles,
    firstName,
    lastName,
    customerNumber,
    companyName,
    isPunchOutSession,
    changeCustomerPageUrl,
    canViewClosedOrders,
    canViewAccountSummaryAndMakePayemnts,
    isChangeCustomerPage,
}) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const myFilteredAccountManagementLinks = filterLinks(
        accountManagementLinks,
        displayChangeCustomerLink,
        canViewClosedOrders,
        isPunchOutSession,
        canViewAccountSummaryAndMakePayemnts,
    );
    const myFilteredOrderManagementLinks = filterLinks(
        orderManagementLinks,
        displayChangeCustomerLink,
        canViewClosedOrders,
        isPunchOutSession,
        canViewAccountSummaryAndMakePayemnts,
    );
    const myFilteredResourcesLinks = filterLinks(
        resourcesLinks,
        displayChangeCustomerLink,
        canViewClosedOrders,
        isPunchOutSession,
        canViewAccountSummaryAndMakePayemnts,
    );

    const linksContainer = (title: string, links: CustomPageLinkModel[] | undefined) => {
        return (
            <StyledWrapper {...styles.linksContainer}>
                {links && links.length > 0 && <Typography {...styles.sectionTitle}>{title}</Typography>}
                <StyledWrapper {...styles.linksSection}>
                    {links?.map(link => {
                        if (link.isBillTrustLink) {
                            return createWidgetElement("MakeAPayment/MakeAPayment", {
                                fields: {
                                    linkTitle: link.title,
                                    customCSS: css`
                                        .make-a-payment-link {
                                            ${TypographyStyle} {
                                                font-size: ${fontConfig.xSmallSingleLine.size}px;
                                                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                                            }
                                        }
                                    `,
                                },
                            });
                        }
                        return (
                            link?.url && (
                                <Link
                                    key={`${link.title}_${link.url}`}
                                    href={`${link?.url}${link.queryString || ""}`}
                                    target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                    aria-label={link?.title}
                                    {...styles.link}
                                >
                                    {link?.title}
                                </Link>
                            )
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
        );
    };
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="header-mini-myAccount">
            <StyledWrapper {...styles.customerInfoWrapper}>
                {(lastName || firstName) && (
                    <Typography {...styles.sectionTitle}>
                        {firstName} {lastName}
                    </Typography>
                )}
                <StyledWrapper {...styles.customerInfoSection}>
                    {customerNumber && (
                        <Typography {...styles.customerNum}>
                            {translate("Account #")} {customerNumber}
                        </Typography>
                    )}
                    <Typography {...styles.companyName}>{companyName}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            <NavigationModeMenu afterSwitchingAction={onCloseMenu} />
            <StyledWrapper {...styles.linksWrapper}>
                {linksContainer(translate("Account Management"), myFilteredAccountManagementLinks)}
                {linksContainer(translate("Product & Order Management"), myFilteredOrderManagementLinks)}
                {linksContainer(translate("Resources"), myFilteredResourcesLinks)}
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                {displayChangeCustomerLink && changeCustomerPageUrl && (
                    <Link
                        {...styles.changeShiptoLink}
                        href={isChangeCustomerPage ? "" : changeCustomerPageUrl}
                        aria-label="Change Ship-to"
                        data-test-selector="headerChangeShipToLink"
                        onClick={onCloseMenu}
                    >
                        {translate("Change Fulfillment")}
                    </Link>
                )}
                <Link
                    {...styles.signOutLink}
                    onClick={onSignOutHandler}
                    data-test-selector="headerSignOutLink"
                    aria-label={isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniMyAccount);
